import React, { useState, useRef } from 'react';
import { Save, Settings as SettingsIcon, Loader2, CheckCircle, Palette, RotateCcw, Upload, Image as ImageIcon, X } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useSettings } from '../../lib/hooks/useSettings';
import { ColorPalette, defaultPalette, applyPalette } from '../../lib/theme';
import MediaPicker from '../../components/admin/MediaPicker';

// Groups of palette keys rendered in the "Цветова палитра" section.
const COLOR_GROUPS: {
  title: string;
  fields: { key: keyof ColorPalette; label: string }[];
}[] = [
  {
    title: 'Основен цвят (Primary)',
    fields: [
      { key: 'primary', label: 'Основен' },
      { key: 'primaryLight', label: 'Светъл' },
      { key: 'primaryDark', label: 'Тъмен' },
    ],
  },
  {
    title: 'Втори цвят (Secondary)',
    fields: [
      { key: 'secondary', label: 'Основен' },
      { key: 'secondaryLight', label: 'Светъл' },
      { key: 'secondaryDark', label: 'Тъмен' },
    ],
  },
  {
    title: 'Акцент (Accent)',
    fields: [
      { key: 'accent', label: 'Основен' },
      { key: 'accentLight', label: 'Светъл' },
      { key: 'accentDark', label: 'Тъмен' },
    ],
  },
];

const Settings = () => {
  const { settings, loading, error, saveSettings } = useSettings();
  const [formData, setFormData] = useState({
    companyName: settings.companyName,
    logoUrl: settings.logoUrl,
    bannerImage: settings.bannerImage,
    description: settings.description,
    catalogLabel: settings.catalogLabel,
    email: settings.email,
    phone: settings.phone,
    address: settings.address,
    socialMedia: {
      facebook: settings.socialMedia.facebook,
      instagram: settings.socialMedia.instagram
    },
    colors: { ...settings.colors }
  });
  // Last persisted palette — used to revert an unsaved live preview on unmount.
  const savedColorsRef = useRef<ColorPalette>(settings.colors);
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showBannerPicker, setShowBannerPicker] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  React.useEffect(() => {
    if (!loading) {
      setFormData({
        companyName: settings.companyName,
        logoUrl: settings.logoUrl,
        bannerImage: settings.bannerImage,
        description: settings.description,
        catalogLabel: settings.catalogLabel,
        email: settings.email,
        phone: settings.phone,
        address: settings.address,
        socialMedia: {
          facebook: settings.socialMedia.facebook,
          instagram: settings.socialMedia.instagram
        },
        colors: { ...settings.colors }
      });
      savedColorsRef.current = settings.colors;
    }
  }, [settings, loading]);

  // On unmount, re-apply the last saved palette so an unsaved live preview does
  // not leak into the rest of the admin until the next full reload.
  React.useEffect(() => {
    return () => {
      applyPalette(savedColorsRef.current);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveStatus({ type: null, message: '' });

    try {
      const success = await saveSettings(formData);
      if (success) {
        // Persisted — this palette is now the one to keep on unmount.
        savedColorsRef.current = formData.colors;
        applyPalette(formData.colors);
        setSaveStatus({
          type: 'success',
          message: 'Настройките са запазени успешно!'
        });
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveStatus({ type: null, message: '' });
        }, 3000);
      } else {
        setSaveStatus({
          type: 'error',
          message: 'Грешка при запазване на настройките'
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus({
        type: 'error',
        message: 'Възникна неочаквана грешка'
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Updates one palette color and applies it immediately for a live preview.
  const handleColorChange = (key: keyof ColorPalette, value: string) => {
    setFormData((prev) => {
      const nextColors = { ...prev.colors, [key]: value };
      applyPalette(nextColors);
      return { ...prev, colors: nextColors };
    });
  };

  // Restores the built-in palette (live preview only; still needs Save to persist).
  const handleResetColors = () => {
    setFormData((prev) => ({ ...prev, colors: { ...defaultPalette } }));
    applyPalette(defaultPalette);
  };

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setSaveStatus({ type: 'error', message: 'Моля, качете изображение' });
      return;
    }
    try {
      setUploadingLogo(true);
      const timestamp = Date.now();
      const safeName = encodeURIComponent(file.name).replace(/%/g, '_');
      const storageRef = ref(storage, `logos/logo_${timestamp}_${safeName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, logoUrl: url }));
    } catch (err) {
      console.error('Error uploading logo:', err);
      setSaveStatus({ type: 'error', message: 'Грешка при качване на логото' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBannerUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setSaveStatus({ type: 'error', message: 'Моля, качете изображение' });
      return;
    }
    try {
      setUploadingBanner(true);
      const timestamp = Date.now();
      const safeName = encodeURIComponent(file.name).replace(/%/g, '_');
      const storageRef = ref(storage, `homepage/banner_${timestamp}_${safeName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, bannerImage: url }));
    } catch (err) {
      console.error('Error uploading banner:', err);
      setSaveStatus({ type: 'error', message: 'Грешка при качване на банера' });
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent as keyof typeof prev],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <SettingsIcon className="w-8 h-8 text-primary mr-3" />
          <h1 className="text-2xl font-bold">Настройки</h1>
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSaving}
          className="flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Запазване...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Запази промените
            </>
          )}
        </button>
      </div>

      {saveStatus.type && (
        <div
          className={`mb-6 p-4 rounded-lg border ${
            saveStatus.type === 'success'
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-red-50 text-red-800 border-red-200'
          }`}
        >
          <div className="flex items-center">
            {saveStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 mr-2" />
            ) : (
              <SettingsIcon className="w-5 h-5 mr-2" />
            )}
            <span>{saveStatus.message}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <h2 className="text-lg font-semibold mb-6 text-gray-800 border-b border-gray-200 pb-2">
              Информация за компанията
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Лого (заменя името в хедъра и footer-а)
              </label>
              <div className="flex flex-wrap items-center gap-4">
                <div className="h-16 w-40 flex-shrink-0 rounded-lg border border-gray-200 bg-neutral-800 flex items-center justify-center overflow-hidden">
                  {formData.logoUrl ? (
                    <img
                      src={formData.logoUrl}
                      alt="Лого"
                      className="max-h-14 max-w-[9rem] object-contain"
                    />
                  ) : (
                    <span className="text-xs text-gray-400">Няма лого</span>
                  )}
                </div>
                <label className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  {uploadingLogo ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 mr-2" />
                  )}
                  Качи лого
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={isSaving || uploadingLogo}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleLogoUpload(file);
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => setShowLogoPicker(true)}
                  className="inline-flex items-center px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <ImageIcon className="w-5 h-5 mr-2" />
                  Избери от медия
                </button>
                {formData.logoUrl && (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, logoUrl: '' }))}
                    className="inline-flex items-center px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Премахни
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Препоръчително: PNG с прозрачен фон и светло лого (хедърът е тъмен). Ако няма лого, се показва името на компанията.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Име на компанията *
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="Име на проекта"
                  required
                  disabled={isSaving}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Кратко описание (показва се във footer-а)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="Апартаменти за продажба в комплекс Златни Пясъци"
                  disabled={isSaving}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Име на каталога (навигация + страница с обектите)
                </label>
                <input
                  type="text"
                  value={formData.catalogLabel}
                  onChange={(e) => handleInputChange('catalogLabel', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="напр. Продажби / Обекти / Имоти"
                  disabled={isSaving}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Показва се в менюто на хедъра, във footer-а и като заглавие на страницата с филтрите (обхваща апартаменти, офиси, магазини).
                </p>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Снимка на банера (страницата с обектите)
                </label>
                <div className="aspect-[16/5] w-full max-w-2xl rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center overflow-hidden mb-3">
                  {formData.bannerImage ? (
                    <img src={formData.bannerImage} alt="Банер" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm text-gray-400">Няма банер — ползва се Hero снимката</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm">
                    {uploadingBanner ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Качи банер
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isSaving || uploadingBanner}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleBannerUpload(file);
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowBannerPicker(true)}
                    className="inline-flex items-center px-3 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors text-sm"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    От медия
                  </button>
                  {formData.bannerImage && (
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, bannerImage: '' }))}
                      className="inline-flex items-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Премахни
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Ако не зададете банер, се използва Hero снимката от началната страница. Препоръчителна пропорция: широка (напр. 1600×500).
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Имейл адрес *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="office@paradise-greenpark.bg"
                  required
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Телефонен номер *
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="0889 66 00 00"
                  required
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Адрес *
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="Златни пясъци, 9007 Варна, България"
                  required
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold mb-6 text-gray-800 border-b border-gray-200 pb-2">
              Социални мрежи
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook URL
                </label>
                <input
                  type="url"
                  value={formData.socialMedia.facebook}
                  onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="https://facebook.com/paradise-greenpark"
                  disabled={isSaving}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram URL
                </label>
                <input
                  type="url"
                  value={formData.socialMedia.instagram}
                  onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                  placeholder="https://instagram.com/paradise-greenpark"
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-6 border-b border-gray-200 pb-2">
              <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                <Palette className="w-5 h-5 mr-2 text-primary" />
                Цветова палитра
              </h2>
              <button
                type="button"
                onClick={handleResetColors}
                disabled={isSaving}
                className="flex items-center text-sm px-3 py-2 text-gray-600 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Върни стандартните
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-6">
              Изберете цвят, за да видите промяната веднага в реално време. Натиснете „Запази промените", за да я приложите за всички посетители на сайта.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {COLOR_GROUPS.map((group) => (
                <div key={group.title} className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700">{group.title}</h3>
                  {group.fields.map((field) => (
                    <div key={field.key} className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.colors[field.key]}
                        onChange={(e) => handleColorChange(field.key, e.target.value)}
                        disabled={isSaving}
                        className="h-10 w-12 flex-shrink-0 cursor-pointer rounded border border-gray-300 bg-white p-1 disabled:cursor-not-allowed"
                        aria-label={`${group.title} - ${field.label}`}
                      />
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-500 mb-1">
                          {field.label}
                        </label>
                        <input
                          type="text"
                          value={formData.colors[field.key]}
                          onChange={(e) => handleColorChange(field.key, e.target.value)}
                          disabled={isSaving}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                          placeholder="#2d5a3d"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 rounded-lg border border-gray-200 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-4">Преглед на живо</p>
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className="btn-primary">Основен бутон</button>
                <button
                  type="button"
                  className="px-6 py-3 rounded-lg font-medium text-white bg-secondary hover:bg-secondary-dark transition-colors"
                >
                  Втори цвят
                </button>
                <span className="px-4 py-2 rounded-lg font-medium bg-accent text-white">Акцент</span>
                <span className="text-primary font-semibold">Текст primary</span>
                <span className="text-secondary font-semibold">Текст secondary</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center px-8 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Запазване...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Запази промените
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-sm font-medium text-blue-800 mb-2">Информация:</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Промените ще се отразят автоматично в публичната част на сайта</li>
          <li>• Логото заменя текстовото име в Header и Footer</li>
          <li>• Контактната информация се използва в Header и Footer</li>
          <li>• Социалните мрежи се показват в Footer-а</li>
          <li>• Цветовата палитра променя основните цветове (бутони, акценти, текст) в целия сайт</li>
          <li>• Всички полета с * са задължителни</li>
        </ul>
      </div>

      <MediaPicker
        isOpen={showLogoPicker}
        onClose={() => setShowLogoPicker(false)}
        onSelect={(url) => setFormData((prev) => ({ ...prev, logoUrl: url }))}
        title="Избери лого от медийната библиотека"
      />

      <MediaPicker
        isOpen={showBannerPicker}
        onClose={() => setShowBannerPicker(false)}
        onSelect={(url) => setFormData((prev) => ({ ...prev, bannerImage: url }))}
        title="Избери снимка за банера"
      />
    </div>
  );
};

export default Settings;